import React from 'react';
import { Link } from 'react-router-dom';
import { GraduationCap, Laptop, Layers, Clock, ArrowLeft, ArrowRight, BookOpen } from 'lucide-react';
import { useLang, tx } from '../i18n/LanguageContext';

const CTA_IMG = 'https://static.prod-images.emergentagent.com/jobs/2f8d28b7-4ebe-4317-8ba5-2c9c19007ec7/images/2c89dd8e0e812a559d8c21fadc8d53d11727b3457e8f878b5983d717c5e3c244.jpeg';

const TOOLS = [
  { label: 'TIA Portal', cls: 'border-blue-200 bg-blue-50/95 text-blue-700' },
  { label: 'CODESYS', cls: 'border-rose-200 bg-rose-50/95 text-rose-700' },
  { label: 'Factory I/O', cls: 'border-emerald-200 bg-emerald-50/95 text-emerald-700' },
  { label: { ar: 'الأتمتة الصناعية', en: 'Industrial Automation' }, cls: 'border-violet-200 bg-violet-50/95 text-violet-700' },
];

const CtaImage = ({ lang }) => (
  <div className="relative min-h-[18rem] sm:min-h-[20rem] lg:min-h-full">
    <div className="absolute inset-0 rounded-2xl lg:rounded-none overflow-hidden lg:[clip-path:polygon(0_0,100%_0,88%_100%,0_100%)] ltr:lg:[clip-path:polygon(12%_0,100%_0,100%_100%,0_100%)]">
      <img src={CTA_IMG} alt={tx(lang, 'مهندس أتمتة يعمل على حاسوبه في مصنع', 'Automation engineer working in a factory')} className="w-full h-full object-cover" />
      <div className="absolute inset-0 bg-gradient-to-t from-slate-900/30 via-transparent to-transparent" />
    </div>
    <svg className="hidden lg:block absolute inset-0 w-full h-full pointer-events-none ltr:-scale-x-100" viewBox="0 0 100 100" preserveAspectRatio="none" aria-hidden="true">
      <polygon points="98.2,0 100,0 88.6,100 86.8,100" fill="#facc15" />
    </svg>
    <div data-testid="cta-band-tools" className="absolute bottom-4 inset-x-4 lg:start-[16%] grid grid-cols-2 sm:grid-cols-4 gap-2">
      {TOOLS.map(({ label, cls }) => {
        const text = typeof label === 'string' ? label : label[lang] || label.ar;
        return <span key={text} className={`flex items-center justify-center text-center min-h-[3rem] rounded-xl border backdrop-blur px-2 py-1.5 text-sm font-extrabold leading-tight shadow-sm ${cls}`}>{text}</span>;
      })}
    </div>
  </div>
);

const Newsletter = () => {
  const { lang, isRTL } = useLang();
  const L = (ar, en) => tx(lang, ar, en);
  const Arrow = isRTL ? ArrowLeft : ArrowRight;
  const feats = [
    { Icon: Laptop, text: L('كورسات احترافية', 'Professional courses') },
    { Icon: Layers, text: L('محاكاة تفاعلية', 'Interactive simulations') },
    { Icon: Clock, text: L('تعلّم من الخبراء', 'Learn from experts') },
  ];

  return (
    <section data-testid="home-cta-band" className="section-b bg-slate-50">
      <div className="site-container">
        <div className="relative overflow-hidden rounded-[1.75rem] bg-gradient-to-l from-white via-white to-blue-50/80 border border-slate-200/70 shadow-[0_20px_60px_-25px_rgba(30,64,175,0.25)] grid lg:grid-cols-[1.05fr_1fr] p-3 lg:p-0 gap-3 lg:gap-0">
          <div className="absolute -top-24 -start-24 w-80 h-80 rounded-full bg-blue-100/60 blur-3xl pointer-events-none" />
          <div className="relative flex flex-col justify-center px-4 py-6 sm:px-8 sm:py-9 lg:ps-12 lg:pe-8">
            <span className="self-start inline-flex items-center gap-2 rounded-full bg-amber-50 border border-amber-200 text-amber-700 text-sm font-bold px-4 py-1.5 mb-5">
              <GraduationCap className="w-4 h-4" />{L('تعلم الأتمتة باحتراف', 'Master automation')}
            </span>
            <h2 className="text-3xl sm:text-4xl lg:text-[2.75rem] leading-[1.3] font-black text-slate-900 tracking-tight mb-4">
              {L('ابدأ رحلتك في عالم', 'Start your journey into')}<br /><span className="text-blue-600">{L('الأتمتة الصناعية', 'industrial automation')}</span>
            </h2>
            <p className="text-slate-600 text-base sm:text-lg leading-relaxed mb-7 max-w-xl">{L('تعلّم مهارات مطلوبة في سوق العمل من خلال محتوى عملي ومحاكاة تفاعلية.', 'Learn in-demand skills through practical content and interactive simulations.')}</p>
            <div className="grid grid-cols-3 gap-3 sm:gap-0 mb-8">
              {feats.map(({ Icon, text }, i) => (
                <div key={text} className={`flex flex-col sm:flex-row items-center sm:items-center gap-2 sm:gap-3 text-center sm:text-start ${i ? 'sm:ps-5 sm:border-s border-slate-200' : ''} ${i < 2 ? 'sm:pe-5' : ''}`}>
                  <Icon className="w-8 h-8 text-blue-600 shrink-0" strokeWidth={1.75} />
                  <span className="text-slate-800 font-bold text-xs sm:text-sm leading-snug">{text}</span>
                </div>
              ))}
            </div>
            <div className="flex flex-col sm:flex-row gap-3">
              <Link to="/subscription" data-testid="cta-band-subscribe" className="btn-subscribe h-14 px-8 text-base sm:text-lg sm:min-w-[15rem]">
                {L('اشترك الآن', 'Subscribe now')}<Arrow className="w-5 h-5" />
              </Link>
              <Link to="/courses" data-testid="cta-band-courses" className="h-14 px-7 rounded-xl border-2 border-slate-200 bg-white text-slate-800 font-bold inline-flex items-center justify-center gap-2 hover:border-blue-300 hover:text-blue-700 transition-colors">
                <BookOpen className="w-5 h-5" />{L('استكشف الكورسات', 'Explore courses')}
              </Link>
            </div>
          </div>
          <CtaImage lang={lang} />
        </div>
      </div>
    </section>
  );
};

export default Newsletter;
